import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { postRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { LocaleContext } from '../../LocaleContext';
import UtilContext from '../../UtilContext';
import { CrmPanelShell } from './panelLayout';
import { filterCrmStepsForTicketType, filterCrmWorkflowStatesByPhase } from '../../../utils/crmCommunicationTypeAliases';
import { displayCrmStateName, displayCrmStepName } from '../../../utils/crmStateI18n';
import { CRM_NEXT_STEP_LABEL_KEY } from '../../../utils/crmCommunicationLevelLabels';
import { runCrmPanelMutation, showCrmWorkflowToast } from '../../../utils/crmCommunicationPanelSave';
import {
  canUserCloseCrmTicketFromManualPhase,
  findCrmClosedStateId,
  ticketStatusMatchesManualClosure,
} from '../../../utils/crmCommunicationClosure';
import AuthContext from '../../AuthContext';

function userLabel(u) {
  return u?.employee?.name || u?.username || `ID ${u?.id ?? ''}`;
}

function isExecutionDoneState(state) {
  return state?.phase === 'execution'
    && (state?.transition_type === 'section_final' || state?.transition_type === 'final_auto');
}

function userCanOverrideCrmWorkflowLock(user) {
  if (!user) return false;
  if (user.is_superuser) return true;
  if (Array.isArray(user.roles) && user.roles.some((r) => String(r?.name || r).toLowerCase() === 'admin')) return true;
  return false;
}

/**
 * Nasledujúci krok – parita FP Vykonanie: oddelenie → zamestnanec → krok → stav kroku.
 */
export default function CrmCommunicationNextStepPanel({
  ticketId,
  ticket = null,
  ticketType = 'acquisition',
  execution,
  crmSteps = null,
  onChanged,
  onCloseTicket = null,
  readOnly = false,
  readOnlyAssignee = false,
  /** Parita FP: krok sa mení podľa next_step / schvaľovateľa, nie podľa stavu Hotová. */
  readOnlyStep = false,
  readOnlyExecutorState = false,
  executionLocked = false,
  embedded = false,
}) {
  const { t, locale } = useContext(LocaleContext);
  const { toast } = useContext(UtilContext);
  const [state] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const users = Array.isArray(state?.users) ? state.users : [];
  const stepDefs = useMemo(
    () => filterCrmStepsForTicketType(crmSteps, ticketType),
    [crmSteps, ticketType],
  );
  const executorStates = useMemo(
    () => filterCrmWorkflowStatesByPhase(state?.crm_states, 'execution', ticketType),
    [state?.crm_states, ticketType],
  );
  const [executorStateId, setExecutorStateId] = useState('');
  const [assignTargetType, setAssignTargetType] = useState('');
  const [stepId, setStepId] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [busy, setBusy] = useState(false);
  const syncingFromServerRef = useRef(false);

  const ns = execution && typeof execution === 'object' ? execution : null;
  const hasExecutorUser = Number(assigneeUserId) > 0
    || Number(ns?.executor_id) > 0
    || Number(ns?.assignee_user_id) > 0;
  const assigneeDisabled = readOnly || readOnlyAssignee || busy || executionLocked;
  const stepDisabled = readOnly || readOnlyStep || busy || executionLocked || !hasExecutorUser;

  const assignedExecutorId = Number(ns?.executor_id ?? ns?.assignee_user_id ?? assigneeUserId ?? 0);

  const closedStateId = useMemo(
    () => findCrmClosedStateId(state?.crm_states),
    [state?.crm_states],
  );

  const showCloseButton = useMemo(
    () => ticketStatusMatchesManualClosure(state?.crm_states, ticket, 'execution', ticketType),
    [state?.crm_states, ticket, ticketType],
  );

  const canCloseTicket = useMemo(
    () => canUserCloseCrmTicketFromManualPhase(ticket, user, state, 'execution', ticketType),
    [ticket, user, state, ticketType],
  );

  useEffect(() => {
    syncingFromServerRef.current = true;
    const crmStepId = ns?.crm_step_id ?? ns?.step_definition_id ?? ns?.step_crm_state_id;
    setStepId(crmStepId != null ? String(crmStepId) : '');
    const uid = ns?.executor_id != null ? Number(ns.executor_id) : (ns?.assignee_user_id != null ? Number(ns.assignee_user_id) : null);
    const did = ns?.department_id != null ? Number(ns.department_id) : null;
    setAssigneeUserId(uid);
    setDepartmentId(did);
    setExecutorStateId(ns?.executor_state != null ? String(ns.executor_state) : '');
    if (uid != null) {
      setAssignTargetType('user');
    } else if (did != null) {
      setAssignTargetType('department');
    } else {
      setAssignTargetType('');
    }
    const timer = setTimeout(() => {
      syncingFromServerRef.current = false;
    }, 0);
    return () => clearTimeout(timer);
  }, [ns]);

  const stepOptions = useMemo(
    () => stepDefs.map((r) => ({ label: displayCrmStepName(r?.name, locale), value: String(r.id) })),
    [locale, stepDefs],
  );

  const executorStateOptions = useMemo(
    () => executorStates.map((r) => ({ label: displayCrmStateName(r?.name, locale), value: String(r.id) })),
    [executorStates, locale],
  );

  const checkExecutorStateLocked = useCallback(() => {
    if (userCanOverrideCrmWorkflowLock(user)) return false;
    const stateId = Number(executorStateId || ns?.executor_state || 0);
    const executorStateDef = (state?.crm_states ?? []).find((row) => Number(row?.id) === stateId);
    if (isExecutionDoneState(executorStateDef)) return true;
    if (ticket?.cancelled_at) return true;
    if (assignedExecutorId <= 0) return true;
    return Number(assignedExecutorId) !== Number(user?.id);
  }, [assignedExecutorId, executorStateId, ns?.executor_state, state?.crm_states, ticket?.cancelled_at, user]);

  const executorStateLockedByRole = checkExecutorStateLocked();
  const canPickExecutorState = !readOnlyExecutorState
    && !busy
    && !executionLocked
    && !!stepId
    && hasExecutorUser
    && !executorStateLockedByRole
    && executorStateOptions.length > 0;

  const deptOptions = useMemo(() => {
    const raw = state?.departments || [];
    return raw.map((d) => ({ name: d.name || `ID ${d.id}`, id: d.id }));
  }, [state?.departments]);

  const deptUserOptions = useMemo(() => {
    if (!departmentId) return [];
    return (users || [])
      .filter((u) => Number(u?.employee?.department_id) === Number(departmentId))
      .map((u) => ({ name: userLabel(u), id: u.id }));
  }, [departmentId, users]);

  const selectedDept = useMemo(
    () => deptOptions.find((d) => Number(d.id) === Number(departmentId)) || null,
    [deptOptions, departmentId],
  );

  const selectedUser = useMemo(
    () => (users || [])
      .filter((u) => Number(u.id) === Number(assigneeUserId))
      .map((u) => ({ name: userLabel(u), id: u.id }))[0] || null,
    [assigneeUserId, users],
  );

  const persistExecutor = useCallback(async (body) => {
    if (assigneeDisabled || syncingFromServerRef.current) return;
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/set_executor`, body, true);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.AssigneeSaved');
          return res;
        } else {
          throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  }, [onChanged, assigneeDisabled, t, ticketId, toast]);

  const persistStep = useCallback(async (body) => {
    if (stepDisabled || syncingFromServerRef.current) return;
    setBusy(true);
    try {
      await runCrmPanelMutation(onChanged, async () => {
        const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/set_executor_step`, body, true);
        if (res?.status === 200) {
          showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.StateSaved');
          return res;
        } else {
          throw new Error(String(res?.message || JSON.stringify(res?.errors || res)));
        }
      });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(e?.message || e), life: 4000 });
    } finally {
      setBusy(false);
    }
  }, [onChanged, stepDisabled, t, ticketId, toast]);

  const onAssignTargetChange = (value) => {
    setAssignTargetType(value);
    setAssigneeUserId(null);
    setDepartmentId(null);
    setStepId('');
    setExecutorStateId('');
  };

  const selectClass = 'bg-white block w-60 px-4 py-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40';
  const dropdownClass = 'w-60 px-4 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40';

  return (
    <CrmPanelShell
      embedded={embedded}
      title={`${t(CRM_NEXT_STEP_LABEL_KEY)}:`}
      t={t}
    >
      <div className={`${embedded ? '' : 'mt-3'} flex flex-col`}>
        <label className="font-normal text-md mb-1">{t('ServiceTaskDetail.DepartmentOrEmployee')}</label>
        <select
          name="next_step_assign_target"
          value={assignTargetType}
          onChange={(e) => onAssignTargetChange(e.target.value)}
          disabled={assigneeDisabled}
          className={`${selectClass} mt-2 mb-1`}
        >
          <option value="">{t('Common.SelectOption')}</option>
          <option value="department">{t('Common.Department')}</option>
          <option value="user">{t('Common.Employee')}</option>
        </select>

        {assignTargetType === 'department' ? (
          <>
            <Dropdown
              value={selectedDept}
              onChange={(e) => {
                const picked = e.value;
                const nextDept = picked?.id != null ? Number(picked.id) : null;
                setDepartmentId(nextDept);
                setAssigneeUserId(null);
                setStepId('');
                setExecutorStateId('');
                if (nextDept) {
                  void persistExecutor({ department_id: nextDept, assignee_user_id: null });
                }
              }}
              options={deptOptions}
              optionLabel="name"
              filter
              placeholder={t('ServiceTaskDetail.SelectDepartments')}
              disabled={assigneeDisabled}
              className={dropdownClass}
            />
            {departmentId ? (
              <Dropdown
                value={deptUserOptions.find((u) => Number(u.id) === Number(assigneeUserId)) || null}
                onChange={(e) => {
                  const picked = e.value;
                  const nextUser = picked?.id != null ? Number(picked.id) : null;
                  setAssigneeUserId(nextUser);
                  if (!nextUser) {
                    setStepId('');
                    setExecutorStateId('');
                    return;
                  }
                  if (departmentId) {
                    void persistExecutor({ department_id: departmentId, assignee_user_id: nextUser });
                  }
                }}
                options={deptUserOptions}
                optionLabel="name"
                filter
                showClear
                placeholder={t('ServiceUsersSettings.SelectUsers')}
                disabled={assigneeDisabled}
                className={dropdownClass}
              />
            ) : null}
          </>
        ) : null}

        {assignTargetType === 'user' ? (
          <Dropdown
            value={selectedUser}
            onChange={(e) => {
              const picked = e.value;
              const nextUser = picked?.id != null ? Number(picked.id) : null;
              setAssigneeUserId(nextUser);
              if (!nextUser) {
                setStepId('');
                setExecutorStateId('');
                return;
              }
              void persistExecutor({ assignee_user_id: nextUser, department_id: null });
            }}
            options={(users || []).map((u) => ({ name: userLabel(u), id: u.id }))}
            optionLabel="name"
            filter
            placeholder={t('ServiceTaskDetail.SelectUser')}
            disabled={assigneeDisabled}
            className={dropdownClass}
          />
        ) : null}

        <label className="font-normal text-md mb-1 mt-3">{t('CrmCommunication.NextStep.Step')}</label>
        <select
          disabled={stepDisabled}
          className={selectClass}
          value={stepId}
          onChange={(e) => {
            const val = e.target.value;
            setStepId(val);
            if (!val || stepDisabled) {
              setExecutorStateId('');
              return;
            }
            setExecutorStateId('');
            void persistStep({ crm_step_id: Number(val) });
          }}
        >
          <option value="">{t('Common.SelectOption')}</option>
          {stepOptions.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        <label className="font-normal text-md mb-1 mt-3">{t('CrmCommunication.Execution.ExecutorState')}</label>
        <select
          disabled={!canPickExecutorState}
          className={selectClass}
          value={executorStateId}
          onChange={async (e) => {
            const val = e.target.value;
            setExecutorStateId(val);
            if (!val || !canPickExecutorState) return;
            setBusy(true);
            try {
              await runCrmPanelMutation(onChanged, async () => {
                const res = await postRequest(`/api/crm_communication_tickets/${ticketId}/execution_state`, { executor_state: Number(val) }, true);
                if (res?.status === 200) {
                  showCrmWorkflowToast(toast, t, 'ServiceTaskDetail.Toast.StateSaved');
                  return res;
                } else {
                  throw new Error(String(res?.message || 'Error'));
                }
              });
            } catch (err) {
              toast?.current?.show({ severity: 'error', summary: t('CrmCommunication.Toast.Error'), detail: String(err?.message || err), life: 4000 });
            } finally {
              setBusy(false);
            }
          }}
        >
          <option value="">{t('Common.SelectOption')}</option>
          {executorStateOptions.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        {showCloseButton && hasExecutorUser && stepId ? (
          <button
            type="button"
            disabled={!canCloseTicket || busy || !closedStateId || typeof onCloseTicket !== 'function'}
            onClick={() => onCloseTicket(closedStateId)}
            className="flex justify-center items-center mt-4 w-60 h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25"
          >
            {t('ServiceTaskDetail.Close')}
          </button>
        ) : null}
      </div>
    </CrmPanelShell>
  );
}
